import { z } from 'zod';
import { JsonObjectSchema, RecordEditorDraftOperationSchema, type RecordEditorDraft } from '@inf-monkeys-tech/monkeys';
import { applyRecordEditorDraft, createRecordEditorDraft, recordEditorValuesEqual } from '@inf-monkeys-tech/monkeys/runtime';

/** Contains only public draft values. The renderer must also reject writes to its secret fields. */
export const RecordEditorDraftPatchSchema = z.object({
  key: z.string().min(1).max(128),
  revision: z.string().min(1).max(256),
  expectedValue: JsonObjectSchema,
  operations: z.array(RecordEditorDraftOperationSchema.refine((operation) => operation.kind !== 'reset', 'Draft patches cannot reset the original.')).min(1).max(32),
}).strict();
export type RecordEditorDraftPatch = z.infer<typeof RecordEditorDraftPatchSchema>;
export type RecordEditorDraftPatchResult = { success: true; draft: RecordEditorDraft } | { success: false; reason: 'stale-revision' | 'stale-value' | 'invalid-operation' };

/** All changes remain local until every operation succeeds. Original and unrelated edits survive. */
export function applyEditorDraftPatch(draft: RecordEditorDraft, input: unknown): RecordEditorDraftPatchResult {
  try {
    const patch = RecordEditorDraftPatchSchema.parse(input);
    if (patch.revision !== draft.revision) return { success: false, reason: 'stale-revision' };
    // Reuse the SDK's JSON, depth and node budget before recursive snapshot comparison.
    createRecordEditorDraft(patch.revision, patch.expectedValue);
    if (!recordEditorValuesEqual(patch.expectedValue, draft.value)) return { success: false, reason: 'stale-value' };
    let next = draft;
    for (const operation of patch.operations) next = applyRecordEditorDraft(next, operation);
    return { success: true, draft: next };
  } catch {
    return { success: false, reason: 'invalid-operation' };
  }
}
